import React, { useCallback, useMemo, useState } from "react";
import { BarChart3, RefreshCw } from "lucide-react";
import { PortalActivityTemplateSummary } from "../api/contracts";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { invalidateClinicianPortalReferenceData } from "../api/portalRequestCache";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { PortalLink } from "../components/PortalLink";
import { getActivityTypePillToken } from "../config/palette";
import { usePortalData } from "../hooks/usePortalData";

type TemplateOverviewStatus = "all" | "on" | "off";

interface ActivityTypeCount {
  activityType: string;
  label: string;
  count: number;
}

const EMPTY_TEMPLATES: readonly PortalActivityTemplateSummary[] = [];

const statusOptions: readonly {
  value: TemplateOverviewStatus;
  label: string;
}[] = [
  { value: "all", label: "All" },
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
];

function normalizedTemplateStatus(template: PortalActivityTemplateSummary) {
  return template.status.trim().toLocaleLowerCase();
}

export function buildActivityTypeCounts(
  templates: readonly PortalActivityTemplateSummary[],
  status: TemplateOverviewStatus,
): ActivityTypeCount[] {
  const counts = new Map<string, number>();

  templates.forEach((template) => {
    if (status !== "all" && normalizedTemplateStatus(template) !== status) {
      return;
    }
    const activityType = template.activityType.trim();
    counts.set(activityType, (counts.get(activityType) ?? 0) + 1);
  });

  return Array.from(counts, ([activityType, count]) => ({
    activityType,
    label: activityType || "Unspecified",
    count,
  })).sort(
    (left, right) =>
      right.count - left.count || left.label.localeCompare(right.label),
  );
}

function activityTemplatesFilterPath(
  status: TemplateOverviewStatus,
  activityType: string,
) {
  const search = new URLSearchParams();
  search.set("status", status);
  search.set("activityType", activityType);
  return `/clinician-portal/activity-templates?${search.toString()}`;
}

function ActivityTypeBarChart({
  counts,
  status,
  clinicianCode,
}: {
  counts: readonly ActivityTypeCount[];
  status: TemplateOverviewStatus;
  clinicianCode: string;
}) {
  if (!counts.length) {
    return (
      <div className="cp-template-overview__empty" role="status">
        <BarChart3 aria-hidden="true" />
        <h3>No templates in this view</h3>
        <p>Choose another availability filter.</p>
      </div>
    );
  }

  const maximumCount = Math.max(...counts.map((item) => item.count));

  return (
    <ol
      className="cp-template-overview-chart"
      aria-label="Templates by Activity Type"
    >
      {counts.map((item) => {
        const content = (
          <>
            <span className="cp-template-overview-chart__label">
              <span
                className="cp-value-pill"
                data-palette={getActivityTypePillToken(item.label)}
              >
                {item.label}
              </span>
            </span>
            <span
              className="cp-template-overview-chart__track"
              aria-hidden="true"
            >
              <span
                className="cp-value-pill cp-template-overview-chart__bar"
                data-palette={getActivityTypePillToken(item.label)}
                style={{
                  width: `${(item.count / maximumCount) * 100}%`,
                }}
              />
            </span>
            <strong>{item.count}</strong>
          </>
        );

        return (
          <li key={item.activityType || "unspecified"}>
            {item.activityType ? (
              <PortalLink
                path={activityTemplatesFilterPath(status, item.activityType)}
                clinicianCode={clinicianCode}
                aria-label={`View ${item.label} templates (${item.count})`}
              >
                {content}
              </PortalLink>
            ) : (
              <div>{content}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function ActivityTemplateOverviewPage({
  clinicianCode,
}: {
  clinicianCode: string;
}) {
  const [statusFilter, setStatusFilter] =
    useState<TemplateOverviewStatus>("all");
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getActivityTemplates(clinicianCode, signal),
    [clinicianCode],
  );
  const state = usePortalData(request);
  const refreshTemplates = state.refresh;
  const refresh = useCallback(() => {
    invalidateClinicianPortalReferenceData("portal.getActivityTemplates");
    refreshTemplates();
  }, [refreshTemplates]);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(data?.found && data.authorized && data.therapist);
  const templates = data?.templates ?? EMPTY_TEMPLATES;
  const statusCounts = useMemo(
    () => ({
      all: templates.length,
      on: templates.filter(
        (template) => normalizedTemplateStatus(template) === "on",
      ).length,
      off: templates.filter(
        (template) => normalizedTemplateStatus(template) === "off",
      ).length,
    }),
    [templates],
  );
  const activityTypeCounts = useMemo(
    () => buildActivityTypeCounts(templates, statusFilter),
    [statusFilter, templates],
  );

  return (
    <ClinicianPortalLayout
      activeGlobalKey="templates"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Activity Template Overview"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              {
                label: "Activity Templates",
                path: "/clinician-portal/activity-templates",
              },
              { label: "Template Overview" },
            ]}
          />
        }
      />

      <div className="cp-page-content cp-page-content--wide">
        {state.status === "loading" ? (
          <LoadingState label="Loading template overview" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState
            error={new Error("Activity templates are not available")}
          />
        ) : null}
        {available ? (
          <section
            className="cp-template-overview"
            aria-label="Activity Template Overview"
          >
            <div className="cp-template-overview__intro">
              <p>
                Review the activity template library by type and availability.
              </p>
              <button
                type="button"
                className="cp-button cp-button--secondary"
                disabled={state.status === "refreshing"}
                onClick={refresh}
              >
                <RefreshCw aria-hidden="true" />
                {state.status === "refreshing" ? "Refreshing…" : "Refresh"}
              </button>
            </div>

            <section
              className="cp-template-overview-summary"
              aria-label="Template summary"
            >
              <div data-tone="total">
                <span>Total Templates</span>
                <strong>{statusCounts.all}</strong>
                <small>Complete template library</small>
              </div>
              <div data-tone="on">
                <span>On</span>
                <strong>{statusCounts.on}</strong>
                <small>Available for assignment</small>
              </div>
              <div data-tone="off">
                <span>Off</span>
                <strong>{statusCounts.off}</strong>
                <small>Unavailable for new assignments</small>
              </div>
            </section>

            <section className="cp-template-overview-card">
              <header>
                <div>
                  <h2>Templates by Activity Type</h2>
                  <p>Number of activity templates in each category.</p>
                </div>
                <div
                  className="cp-history-filters"
                  role="group"
                  aria-label="Template availability filter"
                >
                  {statusOptions.map((option) => (
                    <button
                      type="button"
                      key={option.value}
                      aria-pressed={statusFilter === option.value}
                      onClick={() => setStatusFilter(option.value)}
                    >
                      {option.label} ({statusCounts[option.value]})
                    </button>
                  ))}
                </div>
              </header>
              <ActivityTypeBarChart
                counts={activityTypeCounts}
                status={statusFilter}
                clinicianCode={clinicianCode}
              />
              {activityTypeCounts.length ? (
                <p className="cp-template-overview-card__note">
                  Select an Activity Type to open its filtered template list.
                </p>
              ) : null}
            </section>
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
